"use client";

import {
	CardCarousel,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps
} from "@pamperedchef/design-system";
import { objectHasValues } from "@/app/utilities/objectHasValues";

import * as styles from "./ProductCarousel.module.scss";

export interface ProductCarouselProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	title: HeadingProps;
	cta?: LinkProps<LinkComponentProps>;
	products: {
		image: ImageProps<ImageComponentProps>;
		title: LinkProps<LinkComponentProps>;
		rating?: RatingProps;
		price?: PriceProps;
	}[];
}

/**
 * Product Carousel Component
 */
export function ProductCarousel<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	title,
	cta,
	products
}: ProductCarouselProps<ImageComponentProps, LinkComponentProps>): JSX.Element {
	return (
		<Container>
			{title && (
				<Heading
					{...title}
					className={styles["title"]}
					styledAs={title?.styledAs ?? "h2"}
				/>
			)}
			<CardCarousel
				allowOverflow
				cta={{
					children: cta?.children ?? "",
					href: cta?.href ?? ""
				}}
				slides={
					products?.map((product, index) => (
						<article
							key={index}
							className={styles["product"]}
							aria-labelledby={`product-title-${index}`}
						>
							<Image<ImageComponentProps>
								{...product.image}
								aspectRatio="1:1"
								className={styles["product-image"]}
								alt={product.image.alt || ""}
							/>
							<div className={styles["product-info"]}>
								<Link<LinkComponentProps>
									{...product.title}
									kind="tertiary"
									className={styles["product-title"]}
									id={`product-title-${index}`}
								/>
								{objectHasValues(product.rating) && (
									<Rating {...product.rating} size="small" />
								)}
								{objectHasValues(product.price) && (
									<Price {...product.price} size="small" />
								)}
							</div>
						</article>
					)) ?? []
				}
			/>
		</Container>
	);
}
